import { ApolloError } from "@apollo/client";
import { Flex, Icon, Skeleton, Tooltip, VStack } from "@chakra-ui/react";
import React, { FunctionComponent } from "react";
import { RiExternalLinkLine } from "react-icons/ri";
import { useParams } from "react-router";
import { Link as RouterLink } from "react-router-dom";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminPage } from "src/components/Layout/AdminPage";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { NavigationBlockerProvider } from "src/components/NavigationBlocker";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { SearchAndFilterTypes } from "src/constants";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { Edit } from "./Edit";
import { FormTemplateContext } from "./formTemplateContext";
import {
  GET_FORM_TEMPLATE_BY_ID_FOR_EDIT,
  GET_FORM_TEMPLATE_ORGANIZATION,
} from "./graphql/queries";
import { View } from "./View";

interface Data {
  formTemplate: GQL.EditFormTemplateFragment;
  enrollmentPeriod: GQL.EditFormTemplateFragment_enrollment_period;
  organization: GQL.Organization;
}

type Props = { mode: "edit" | "view" };

export const FormTemplate: FunctionComponent<Props> = ({ mode }) => {
  const { formTemplateId = "" } = useParams();

  const { remoteData: gqlData } = useRemoteDataQuery<
    GQL.GetFormTemplateByIdForEdit,
    GQL.GetFormTemplateByIdForEditVariables
  >(GET_FORM_TEMPLATE_BY_ID_FOR_EDIT, {
    variables: { form_template_id: formTemplateId },
    fetchPolicy: "network-only",
  });

  const { remoteData: organizationData } = useRemoteDataQuery<
    GQL.GetFormTemplateOrganization,
    GQL.GetFormTemplateOrganizationVariables
  >(GET_FORM_TEMPLATE_ORGANIZATION, {
    variables: { formTemplateId: formTemplateId },
    fetchPolicy: "network-only",
  });

  const remoteData = React.useMemo(() => {
    return RD.toTuple(
      gqlData.mapError<ApolloError>((error) => error),
      organizationData.mapError<ApolloError>((error) => error)
    ).andThen(([data, organizationData]) => {
      if (!data.form_template_by_pk) {
        return RD.success<ApolloError, Data | null>(null);
      }
      try {
        return RD.success<ApolloError, Data>({
          formTemplate: data.form_template_by_pk,
          enrollmentPeriod: data.form_template_by_pk.enrollment_period,
          organization: organizationData.organization[0] as GQL.Organization,
        });
      } catch (error: unknown) {
        console.error(error);
        return RD.failure<ApolloError, Data>(
          new ApolloError({ errorMessage: "Cannot parse form" })
        );
      }
    });
  }, [gqlData, organizationData]);

  const menuRef = React.useRef<HTMLDivElement>(null);
  const actionRef = React.useRef<HTMLDivElement>(null);

  return (
    <NavigationBlockerProvider>
      <GQLRemoteDataView<Data | null>
        remoteData={remoteData}
        reloading={null}
        loading={<Loader />}
      >
        {(data) => {
          if (data === null) {
            return <NotFound />;
          }

          const { enrollmentPeriod, formTemplate, organization } = data;
          const newSearchParams = new URLSearchParams();
          newSearchParams.set(
            SearchAndFilterTypes.EnrollmentPeriod,
            formTemplate.enrollment_period.id
          );
          const formReponsesUrl = Url.OrgAdmin.Forms.index({
            organization,
            formTemplateId: formTemplate.id,
            params: newSearchParams.toString(), // Enrollment period is required to be set for the page to load successfully
          });

          return (
            <FormTemplateContext.Provider
              value={{
                menuRef,
                actionRef,
                formTemplate,
                organization,
                mode,
              }}
            >
              <AdminPage
                breadcrumb={
                  <Breadcrumb
                    items={breadcrumb.enrollmentPeriod.getBreadcrumbsForEditFormTemplate(
                      RD.success(organization),
                      enrollmentPeriod,
                      formTemplate
                    )}
                  />
                }
                heading={
                  <Flex direction="column" gap="2">
                    <AdminHeading
                      title={formTemplate.name}
                      titleIcon={
                        formTemplate.mode ===
                          GQL.form_template_mode_enum.Published && (
                          <Tooltip
                            hasArrow
                            placement="top"
                            label="Go to form responses for this form in a new tab"
                            textAlign="center"
                          >
                            <RouterLink to={formReponsesUrl} target="_blank">
                              <Icon
                                fontSize="lg"
                                color="primary.600"
                                as={RiExternalLinkLine}
                              />
                            </RouterLink>
                          </Tooltip>
                        )
                      }
                      id={formTemplate.id}
                      actionButton={
                        <>
                          {/*
                        actionRef container is used by child components via Portal to render action buttons.
                        See: src/scenes/orgAdmin/formTemplates/ContentTab/V2/Content.tsx
                        where it renders the Publish button using <Portal containerRef={actionRef}>
                      */}
                          <Flex gap="4" ref={actionRef} />
                        </>
                      }
                    />
                    {/*
                    menuRef container is used by child components via Portal to render Draft/Published menu.
                    See: src/scenes/orgAdmin/formTemplates/ContentTab/V2/Content.tsx
                    where it renders the ModeMenu using <Portal containerRef={menuRef}>
                  */}
                    <Flex direction="column" ref={menuRef} gap="4" />
                  </Flex>
                }
              >
                {mode === "view" ? (
                  <View formTemplate={data.formTemplate} />
                ) : (
                  <Edit formTemplate={data.formTemplate} />
                )}
              </AdminPage>
            </FormTemplateContext.Provider>
          );
        }}
      </GQLRemoteDataView>
    </NavigationBlockerProvider>
  );
};

const Loader: React.FC = () => {
  return (
    <Flex direction="column" gap={16}>
      <VStack align="flex-start">
        <Skeleton height="2rem" width="15rem" />
        <Skeleton height="1rem" width="20rem" />
      </VStack>
      <Flex direction="column" gap={3}>
        <Skeleton height="2rem" width="15rem" />
        <Skeleton height="8rem" width="100%" />
        <Skeleton height="8rem" width="100%" />
        <Skeleton height="8rem" width="100%" />
      </Flex>
    </Flex>
  );
};
